import { z } from 'zod';

/**
 * Novita provider options schema.
 *
 * Novita exposes an OpenAI-compatible Chat Completions API. These options cover
 * documented Novita-specific and OpenAI-compatible parameters, while
 * passthrough keeps the provider forward-compatible with new API fields.
 */
export const novitaProviderOptions = z.object({
  /** Number of completions to generate. */
  n: z.number().int().min(1).max(128).optional(),

  /** Sampling seed for best-effort determinism. */
  seed: z.number().int().optional(),

  /** Penalize repeated tokens based on frequency. */
  frequency_penalty: z.number().min(-2).max(2).optional(),

  /** Penalize repeated tokens based on presence. */
  presence_penalty: z.number().min(-2).max(2).optional(),

  /** Novita repetition penalty. */
  repetition_penalty: z.number().gt(0).lt(2).optional(),

  /** Top-k sampling limit. */
  top_k: z.number().int().gt(1).lt(128).optional(),

  /** Minimum probability threshold relative to the most likely token. */
  min_p: z.number().min(0).max(1).optional(),

  /** Token logit biases. */
  logit_bias: z.record(z.string(), z.number().int().min(-100).max(100)).optional(),

  /** Return output token log probabilities. */
  logprobs: z.boolean().optional(),

  /** Number of top logprobs to return when `logprobs` is enabled. */
  top_logprobs: z.number().int().min(0).max(20).optional(),

  /** Request reasoning content separately when supported by the selected model. */
  separate_reasoning: z.boolean().optional(),

  /** Enable or disable model thinking when supported by the selected model. */
  enable_thinking: z.boolean().optional(),

  /** Output modalities for models that can return audio. */
  modalities: z.array(z.string()).optional(),
}).passthrough();

export type NovitaProviderOptions = z.infer<typeof novitaProviderOptions>;
